// @ts-check
import React from 'react';
import Modal from 'shared/components/Modal';
import { Link } from 'react-router';
import cx from 'classnames';
import ScheduledAt from './ScheduledAt';
import ChecklistProgress from 'shared/components/Referrals/IndexPage/ChecklistProgress';
import ReferralsTable from './ReferralsTable';
import FileAttachments from './FileAttachments';
import ReferralChecklist from 'shared/components/Referrals/ReferralChecklist';
import QuickAssignOrSchedule from 'shared/components/Referrals/QuickAssignOrSchedule';
import DropdownMenu, {
  DropdownTrigger,
  DropdownContent,
} from 'agent_portal/layouts/FuturePortalLayout/DropdownMenu';
import FileUploader from 'shared/components/Referrals/FileUploader';
import AppointmentCanceller from 'shared/components/Referrals/AppointmentCanceller';
import AppointmentNoShower from 'shared/components/Referrals/AppointmentNoShower';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import AsyncReferralQuestionnaire from 'shared/components/Referrals/AsyncReferralQuestionnaire';
import api from 'utils/api';
import pluralize from 'pluralize';
import styles from './IndexPage.module.scss';
import Icon from 'shared/components/Icon';
import { FAIcon } from 'preferral-ui/components/FAIcon';
import { useMutation, useQuery } from 'react-query';
import { AddTagDropdown } from './AddTagDropdown';

const ReferralTag = (props) => {
  const { referralTag, onDelete } = props;
  const { id, label, color } = referralTag;

  return (
    <span
      data-id={`ReferralTag--${id}`}
      style={{ backgroundColor: color }}
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
    >
      {label}
      <span className="ml-1">
        <button
          type="button"
          className="bg-none outline-none cursor-pointer"
          onClick={onDelete}
        >
          <FAIcon icon="times" />
        </button>
      </span>
    </span>
  );
};

const ReferralRow = (props) => {
  const {
    referral,
    showModal,
    confirmReferral,
    onRemoveReferralTag,
    onAddReferralTag,
    allReferralTags,
    tagsLoaded,
  } = props;

  const checklistPresent =
    referral.serializedReferralChecklist &&
    referral.serializedReferralChecklist.checklistItems &&
    referral.serializedReferralChecklist.checklistItems.length > 0;

  const availableReferralTags = unusedTags(
    referral.referralTags,
    allReferralTags
  );

  return (
    <tr
      id={`referral-${referral.id}`}
      className={cx(referral.referralPriorityCssClass, {
        [styles.reminders_awake]: referral.reminderStatus === 'awake',
        [styles.reminders_asleep]: referral.reminderStatus === 'asleep',
        [styles.reminders_completed]: referral.reminderStatus === 'completed',
        [styles.stat]: referral.stat,
        [styles.unbooked]: !referral['booked?'],
        [styles.active]: referral['active?'],
      })}
    >
      <td className={styles.reminder_status}>
        {referral.reminderStatus === 'completed' && (
          <Icon name="checkmark" cssTip="All reminders have been completed" />
        )}
        {referral.reminderStatus === 'awake' && (
          <Icon name="bell" cssTip="A reminder is due for completion" />
        )}
        {referral.reminderStatus === 'asleep' && (
          <Icon
            name="hour-glass"
            cssTip="There is an incomplete scheduled reminder"
          />
        )}
        {referral.reminderStatus === null && (
          <Icon name="radio-unchecked" cssTip="There are no reminders" />
        )}
      </td>
      <td>
        <Link
          className="btn btn-mini btn-info"
          to={`/a/portal/referrals/${referral.id}`}
        >
          View Details
        </Link>
        <br />
        {referral.fileAttachments.length > 0 ? (
          <span className="secondaryData">
            <span
              className="fake-link"
              onClick={() => showModal('viewFileAttachments', referral.id)}
            >
              {pluralize('file', referral.fileAttachments.length, true)}
            </span>
          </span>
        ) : null}
        {!referral.enteredByGuest && !referral.enteredByReceiver && (
          <span
            className="secondaryData dataTooltip"
            data-tip="The sender of this referral is from a verified AristaMD account."
            style={{ color: 'green' }}
          >
            Sender Verified <Icon name="check-circle" />
          </span>
        )}
      </td>
      <td className={referral.referralPriorityCssClass}>
        <div className={styles.direction_icon_container}>
          <div className={styles.icon_column}>
            {referral.enteredByGuest ? (
              <span className={styles.icon_container}>
                <Icon
                  name="file-text2"
                  cssTip="Referral entered by guest via a AristaMD Powered Form"
                />
              </span>
            ) : referral.enteredByReceiver ? (
              <span className={styles.icon_container}>
                <Icon
                  name="download"
                  cssTip="Referral entered as inbound by internal staff"
                />
              </span>
            ) : null}
          </div>
          <div className={styles.label_column}>{referral.createdAt}</div>
        </div>

        <span className="secondaryData">{referral.specialtyName}</span>
        <br />
        <span className="secondaryData">
          {referral['hasQuestionnaire?'] && (
            <span
              className="fake-link"
              onClick={() => showModal('questionnaire', referral.id)}
            >
              Questionnaire
            </span>
          )}
        </span>
      </td>
      <td>
        <strong>{referral.fullName}</strong>
        <br />
        <span className="secondaryData">
          Referred From: {referral.referringProviderName}
        </span>
        <div className="flex mt-2 -mb-1">
          <div className="flex flex-wrap flex-grow gap-1">
            {(referral.referralTags || []).map((referralTag) => (
              <div key={referralTag.id} className="mb-1">
                <ReferralTag
                  referralTag={referralTag}
                  onDelete={() =>
                    onRemoveReferralTag(referral.id, referralTag.id)
                  }
                />
              </div>
            ))}
          </div>
          <div className="flex-shrink-0">
            {tagsLoaded ? (
              <AddTagDropdown
                items={availableReferralTags}
                onSelect={(tag) => onAddReferralTag(referral.id, tag.id)}
              />
            ) : null}
          </div>
        </div>
      </td>
      <td data-spec-class="receiving-provider">
        {referral.receivingProviderName}
        <br />
        <span className="secondaryData">{referral.receivingLocationName}</span>
      </td>
      <td>
        <span className={referral.statusBootstrapClass}>
          {referral.statusName}
        </span>
        {referral.stat && (
          <span>
            {' '}
            <span className={cx(styles.stat, 'label')}>STAT</span>
          </span>
        )}
        <br />
        <ScheduledAt
          referral={referral}
          showScheduler={() => showModal('schedule', referral.id)}
        />
        <br />
        {referral['appointmentUnconfirmed?'] && (
          <strong>
            <span
              className="secondaryData fake-link"
              onClick={() => confirmReferral(referral)}
            >
              Confirm
            </span>
          </strong>
        )}
      </td>
      <td className="insuranceCoverages">
        <span className="secondaryData">
          <ul>
            {referral.insuranceCoverages.map((ic) => (
              <li key={ic.id}>
                {ic.payerName}
                <br />[{ic.planName}]
              </li>
            ))}
            {referral['isSelfPay?'] && <li>Self Pay</li>}
          </ul>
        </span>
      </td>
      <td className="taskStatus">
        <div>
          {referral.assignedAgent ? (
            <p style={{ textWeight: 500, textAlign: 'left' }}>
              <span style={{ marginRight: '0.25rem' }}>
                <Icon
                  name="user"
                  cssTip={`${referral.assignedAgent.name} is assigned to manage this referral.`}
                />
              </span>{' '}
              {referral.assignedAgent.name}
            </p>
          ) : (
            <p style={{ textAlign: 'left', color: '#aaa' }}>
              No assigned agent
            </p>
          )}
        </div>
        <div
          className={cx({ 'button-td': checklistPresent })}
          onClick={() =>
            checklistPresent && showModal('checklist', referral.id)
          }
        >
          {checklistPresent && (
            <span className="myTooltip">Click to edit checklist</span>
          )}
          {checklistPresent && (
            <ChecklistProgress
              checklist={referral.serializedReferralChecklist}
            />
          )}
        </div>
      </td>
      <td>
        <DropdownMenu>
          <DropdownTrigger>
            <span className="fake-link">More...</span>
          </DropdownTrigger>
          <DropdownContent>
            <div>
              <ul data-spec-class="more-menu">
                {referral['hasOrganization?'] && referral['active?'] && (
                  <li>
                    <a
                      data-spec-class="schedule-link"
                      onClick={() => showModal('schedule', referral.id)}
                    >
                      {referral['booked?']
                        ? 'Reassign/Reschedule'
                        : 'Assign/Schedule'}
                    </a>
                  </li>
                )}
                <li>
                  <a onClick={() => showModal('file', referral.id)}>
                    Upload Files
                  </a>
                </li>
                {referral['appointmentCancellable?'] && (
                  <li className="danger">
                    <a onClick={() => showModal('cancel', referral.id)}>
                      Cancel
                    </a>
                  </li>
                )}
                {referral['appointmentNoShowable?'] && (
                  <li className="danger">
                    <a onClick={() => showModal('noshow', referral.id)}>
                      No Show
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </DropdownContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};

const TableComponent = (props) => {
  const { referrals, refresh } = props;

  const [referralId, setReferralId] = React.useState(null);
  const [openModal, setOpenModal] = React.useState(null);

  const hideModal = React.useCallback(() => setOpenModal(null), []);
  const showModal = React.useCallback((name, referralId) => {
    setReferralId(referralId);
    setOpenModal(name);
  }, []);

  const handleSuccessfulModal = React.useCallback(() => {
    refresh();
    hideModal();
  }, [refresh, hideModal]);

  const referralTagsQuery = useQuery('referral-tags', () =>
    api.get('/agent_portal/api/referral_tags').then((resp) => resp.data)
  );

  const allReferralTags =
    (referralTagsQuery.data && referralTagsQuery.data.referralTags) || [];

  const confirmReferralMut = useMutation((referral) =>
    api
      .post(
        `/agent_portal/api/appointments/${referral.appointmentId}/confirmation`
      )
      .then((resp) => resp.data)
  );

  const confirmReferral = React.useCallback(
    (referral) => {
      return confirmReferralMut.mutateAsync(referral).then((data) => {
        refresh();
        FlashMessageActions.flashSuccess({
          message: 'You have confirmed an appointment',
        });
      });
    },
    [refresh]
  );

  const removeReferralTagMut = useMutation((
    /** @type {{ referralId: string, referralTagId: string }} */ removeTagInput
  ) =>
    api
      .delete(
        `/agent_portal/api/referrals/${removeTagInput.referralId}/taggings/${removeTagInput.referralTagId}`
      )
      .then((resp) => resp.data)
  );

  const removeReferralTag = React.useCallback(
    (referralId, referralTagId) => {
      return removeReferralTagMut
        .mutateAsync({ referralId, referralTagId })
        .then((data) => {
          refresh();
          FlashMessageActions.flashSuccess({ message: 'Tag removed' });
        });
    },
    [refresh]
  );

  const addReferralTagMut = useMutation((
    /** @type {{ referralId: string, referralTagId: string }} */ addTagInput
  ) =>
    api
      .post(`/agent_portal/api/referrals/${addTagInput.referralId}/taggings`, {
        referral_tag_id: addTagInput.referralTagId,
      })
      .then((resp) => resp.data)
  );

  const addReferralTag = React.useCallback(
    (referralId, referralTagId) => {
      return addReferralTagMut
        .mutateAsync({ referralId, referralTagId })
        .then((data) => {
          refresh();
          FlashMessageActions.flashSuccess({ message: 'Tag added' });
        });
    },
    [refresh]
  );

  const referral = referralId && referrals.find((r) => r.id === referralId);

  return (
    <div>
      {referral && (
        <div>
          <Modal show={openModal === 'checklist'} onHide={hideModal}>
            <ReferralChecklist
              referralId={referralId}
              apiEndpoint="/agent_portal/api"
              checkable
              editable={false}
              handleSuccess={refresh}
            />
          </Modal>
          <Modal show={openModal === 'schedule'} onHide={hideModal}>
            <QuickAssignOrSchedule
              referralId={referralId}
              handleSuccess={handleSuccessfulModal}
              webpageBase="/a/portal"
              apiEndpoint="/agent_portal/api"
              allowGiveTime
              showProviderSearch
            />
          </Modal>
          <Modal show={openModal === 'file'} onHide={hideModal}>
            <FileUploader
              referralId={referralId}
              handleSuccess={handleSuccessfulModal}
              apiEndpoint="/agent_portal/api"
            />
          </Modal>
          <Modal show={openModal === 'cancel'} onHide={hideModal}>
            <AppointmentCanceller
              appointmentId={referral.appointmentId}
              handleSuccess={handleSuccessfulModal}
              apiEndpoint="/agent_portal/api"
            />
          </Modal>
          <Modal show={openModal === 'noshow'} onHide={hideModal}>
            <AppointmentNoShower
              appointmentId={referral.appointmentId}
              handleSuccess={handleSuccessfulModal}
              apiEndpoint="/agent_portal/api"
            />
          </Modal>
          <Modal show={openModal === 'questionnaire'} onHide={hideModal}>
            <AsyncReferralQuestionnaire referralId={referralId} />
          </Modal>
          <Modal show={openModal === 'viewFileAttachments'} onHide={hideModal}>
            <div className="modal-header">
              <h3>Files</h3>
            </div>
            <div className="modal-body">
              <FileAttachments referral={referral} handleSuccess={refresh} />
            </div>
          </Modal>
        </div>
      )}
      <table className="table-bordered table" data-spec-class="referrals">
        <thead>
          <tr>
            <th />
            <th>Details</th>
            <th>Referral</th>
            <th>Patient</th>
            <th>Referred To</th>
            <th>Status</th>
            <th>Payer</th>
            <th>Checklist</th>
            <th>More</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((r) => (
            <ReferralRow
              key={r.id}
              referral={r}
              allReferralTags={allReferralTags}
              tagsLoaded={referralTagsQuery.isFetched}
              refetch={refresh}
              showModal={showModal}
              confirmReferral={confirmReferral}
              onRemoveReferralTag={removeReferralTag}
              onAddReferralTag={addReferralTag}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReceivedReferralsTable = (props) => (
  <ReferralsTable {...props} tableComponent={TableComponent} />
);

export default ReceivedReferralsTable;

function unusedTags(currentTags, allTags) {
  const currentIds = currentTags.map((t) => t.id);
  return allTags.filter((t) => !currentIds.includes(t.id));
}
